import { ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { formatDate } from "@/lib/format";
import { cn } from "@/lib/utils";
import type { WorkOrder, WorkOrderSetValues, WorkOrderStatus } from "@/mocks/search-work-orders";

const STATUS_CHIP_CLASSNAME: Record<WorkOrderStatus, string> = {
  "Not Started": "bg-muted text-muted-foreground",
  "In Process": "bg-[var(--info-bg)] text-[var(--info)]",
  "Build Complete": "bg-[var(--success-bg)] text-[var(--success)]",
  Closed: "bg-[var(--warning-bg)] text-[var(--warning)]",
};

export function WorkOrderStatusChip({ status }: { status: WorkOrderStatus }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap",
        STATUS_CHIP_CLASSNAME[status],
      )}
    >
      {status}
    </span>
  );
}

// The "changing" cell treatment mirrors the design system's td.cell-changed
// spec (bg-accent, semibold) — applied per-column based on which Set field
// (if any) is filled in for this table.
const CHANGED_CELL_CLASSNAME = "bg-[var(--bg-accent)] font-semibold";

interface WorkOrderTransferTableProps {
  title: string;
  countText: string;
  rows: WorkOrder[];
  emptyMessage: string;
  checkedIds: Set<string>;
  onToggleRow: (id: string, checked: boolean) => void;
  onSelectAll: () => void;
  onSelectNone: () => void;
  moveLabel: string;
  moveDisabled: boolean;
  onMove: () => void;
  onRowClick: (row: WorkOrder) => void;
  /** Only passed for the "Selected for update" table — drives cell-changed highlighting. */
  setValues?: WorkOrderSetValues;
}

export function WorkOrderTransferTable({
  title,
  countText,
  rows,
  emptyMessage,
  checkedIds,
  onToggleRow,
  onSelectAll,
  onSelectNone,
  moveLabel,
  moveDisabled,
  onMove,
  onRowClick,
  setValues,
}: WorkOrderTransferTableProps) {
  const isRightward = moveLabel.trim().endsWith("→");
  const statusChanging = !!setValues?.status;
  const locationChanging = !!setValues?.locationCode;
  const startChanging = !!setValues?.startDate;
  const endChanging = !!setValues?.endDate;

  return (
    <Card className="flex flex-col">
      <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h3 className="text-sm font-semibold">{title}</h3>
          <p className="text-xs text-muted-foreground">{countText}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onSelectAll} disabled={rows.length === 0}>
            Select all
          </Button>
          <Button variant="ghost" size="sm" onClick={onSelectNone} disabled={checkedIds.size === 0}>
            Select none
          </Button>
          <Button size="sm" onClick={onMove} disabled={moveDisabled}>
            {isRightward ? null : <ArrowLeft className="size-4" />}
            {moveLabel.replace("→", "").replace("←", "").trim()}
            {isRightward ? <ArrowRight className="size-4" /> : null}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {rows.length === 0 ? (
          <p className="rounded-lg border border-dashed py-10 text-center text-sm text-muted-foreground">
            {emptyMessage}
          </p>
        ) : (
          <div className="max-h-96 overflow-auto rounded-lg border">
            <table className="w-full border-collapse text-sm">
              <thead className="sticky top-0 bg-muted text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="w-8 px-3 py-2 text-left">
                    <span className="sr-only">Select</span>
                  </th>
                  <th className="px-3 py-2 text-left">WO #</th>
                  <th className="px-3 py-2 text-left">Item</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Start date</th>
                  <th className="px-3 py-2 text-left">End date</th>
                  <th className="px-3 py-2 text-left">Location</th>
                  <th className="px-3 py-2 text-right">Qty</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.workOrderId}
                    onClick={() => onRowClick(row)}
                    className="cursor-pointer border-t hover:bg-muted/50"
                  >
                    <td className="px-3 py-2" onClick={(event) => event.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label={`Select ${row.workOrderNo}`}
                        checked={checkedIds.has(row.workOrderId)}
                        onChange={(event) => onToggleRow(row.workOrderId, event.target.checked)}
                        className="size-4 rounded border-input accent-[var(--brand-accent)]"
                      />
                    </td>
                    <td className="px-3 py-2 font-medium whitespace-nowrap">{row.workOrderNo}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{row.itemCode}</td>
                    <td className={cn("px-3 py-2", statusChanging && CHANGED_CELL_CLASSNAME)}>
                      <WorkOrderStatusChip status={row.status} />
                    </td>
                    <td
                      className={cn(
                        "px-3 py-2 whitespace-nowrap",
                        startChanging && CHANGED_CELL_CLASSNAME,
                      )}
                    >
                      {formatDate(row.startDate)}
                    </td>
                    <td
                      className={cn(
                        "px-3 py-2 whitespace-nowrap",
                        endChanging && CHANGED_CELL_CLASSNAME,
                      )}
                    >
                      {formatDate(row.endDate)}
                    </td>
                    <td
                      className={cn(
                        "px-3 py-2 whitespace-nowrap",
                        locationChanging && CHANGED_CELL_CLASSNAME,
                      )}
                    >
                      {row.locationName}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">{row.quantity}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
